import { useCallback, useEffect, useRef, useState } from 'react';
import { errorReporter } from '@/services/errorReportingService';

interface RequestStreamOptions {
  deviceId?: string;
  audioDeviceId?: string;
  facing?: 'user' | 'environment';
  maxWidth?: number;
  maxHeight?: number;
  frameRate?: number;
}

interface UseMediaStreamReturn {
  stream: MediaStream | null;
  rawStream: MediaStream | null;
  previewRef: React.RefObject<HTMLVideoElement>;
  availableCameras: MediaDeviceInfo[];
  selectedCameraId: string | null;
  preferredFacingMode: 'user' | 'environment';
  isSwitchingCamera: boolean;
  cameraWarning: string | null;
  availableMics: MediaDeviceInfo[];
  selectedMicId: string | null;
  isSwitchingMic: boolean;
  micWarning: string | null;
  error: string | null;
  selectCamera: (deviceId: string, options?: Omit<RequestStreamOptions, 'deviceId'>) => Promise<void>;
  selectMic: (deviceId: string, options?: Omit<RequestStreamOptions, 'audioDeviceId'>) => Promise<void>;
  refreshCameras: () => Promise<MediaDeviceInfo[]>;
  refreshMics: () => Promise<MediaDeviceInfo[]>;
  requestStream: (options?: RequestStreamOptions) => Promise<MediaStream | null>;
  cleanupStream: () => void;
  clearError: () => void;
  clearCameraWarning: () => void;
  clearMicWarning: () => void;
}

export function useMediaStream(): UseMediaStreamReturn {
  const previewRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  /** Guards against `getUserMedia` resolving after unmount and orphaning a live stream. */
  const isUnmountedRef = useRef(false);
  const refreshCamerasInFlightRef = useRef<boolean>(false);
  const refreshMicsInFlightRef = useRef<boolean>(false);
  const requestChainRef = useRef<Promise<MediaStream | null>>(Promise.resolve(null));

  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [preferredFacingMode, setPreferredFacingMode] = useState<'user' | 'environment'>('user');
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [cameraWarning, setCameraWarning] = useState<string | null>(null);
  const [availableMics, setAvailableMics] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('notego_preferred_mic') ?? null;
    } catch {
      return null;
    }
  });
  const [isSwitchingMic, setIsSwitchingMic] = useState(false);
  const [micWarning, setMicWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rawStream, setRawStream] = useState<MediaStream | null>(null);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setRawStream(null);
    if (previewRef.current) {
      previewRef.current.srcObject = null;
    }
  }, []);

  const refreshCameras = useCallback(async (): Promise<MediaDeviceInfo[]> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setAvailableCameras([]);
      return [];
    }

    // Prevent overlapping calls
    if (refreshCamerasInFlightRef.current) {
      return [];
    }

    refreshCamerasInFlightRef.current = true;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((device) => device.kind === 'videoinput');
      setAvailableCameras(videoInputs);
      setCameraWarning(null);
      setSelectedCameraId((current) => {
        if (current && videoInputs.some((device) => device.deviceId === current)) {
          return current;
        }

        if (videoInputs.length === 0) {
          return null;
        }

        const activeTrack = streamRef.current?.getVideoTracks()[0];
        const activeId = activeTrack?.getSettings().deviceId;
        return activeId ?? videoInputs[0].deviceId;
      });
      return videoInputs;
    } catch (err) {
      errorReporter.expected('Failed to enumerate camera devices', err, {
        feature: 'video',
        operation: 'refreshCameras',
      });
      setAvailableCameras([]);
      setCameraWarning('Unable to detect multiple cameras. Camera switching may be limited.');
      return [];
    } finally {
      refreshCamerasInFlightRef.current = false;
    }
  }, []);

  const refreshMics = useCallback(async (): Promise<MediaDeviceInfo[]> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setAvailableMics([]);
      return [];
    }

    if (refreshMicsInFlightRef.current) {
      return [];
    }

    refreshMicsInFlightRef.current = true;

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((device) => device.kind === 'audioinput');
      setAvailableMics(audioInputs);
      setMicWarning(null);
      setSelectedMicId((current) => {
        if (current && audioInputs.some((device) => device.deviceId === current)) {
          return current;
        }

        if (audioInputs.length === 0) {
          return null;
        }

        const activeTrack = streamRef.current?.getAudioTracks()[0];
        const activeId = activeTrack?.getSettings().deviceId;
        return activeId ?? audioInputs[0].deviceId;
      });
      return audioInputs;
    } catch (err) {
      errorReporter.expected('Failed to enumerate audio devices', err, {
        feature: 'audio',
        operation: 'refreshMics',
      });
      setAvailableMics([]);
      setMicWarning('Unable to detect multiple microphones. Microphone switching may be limited.');
      return [];
    } finally {
      refreshMicsInFlightRef.current = false;
    }
  }, []);

  const acquireStream = useCallback(
    async (options: RequestStreamOptions = {}): Promise<MediaStream | null> => {
      const { deviceId, audioDeviceId, facing, maxHeight, maxWidth, frameRate } = options;

      const resolutionConstraints: MediaTrackConstraints = {};
      if (maxWidth) {
        resolutionConstraints.width = { ideal: maxWidth, max: maxWidth };
      }
      if (maxHeight) {
        resolutionConstraints.height = { ideal: maxHeight, max: maxHeight };
      }
      if (frameRate) {
        resolutionConstraints.frameRate = { ideal: frameRate };
      }

      const videoConstraints: MediaTrackConstraints = deviceId
        ? { deviceId: { exact: deviceId }, ...resolutionConstraints }
        : { facingMode: facing ?? preferredFacingMode, ...resolutionConstraints };

      try {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
          setError('Camera access is not supported in this browser.');
          return null;
        }

        let preferredMic = audioDeviceId;
        if (!preferredMic) {
          try {
            preferredMic = localStorage.getItem('notego_preferred_mic') ?? undefined;
          } catch {
            /* noop */
          }
        }
        const audioConstraints: MediaTrackConstraints | true = preferredMic
          ? { deviceId: { exact: preferredMic } }
          : true;

        // Stop previous stream BEFORE acquiring new one to release hardware locks.
        // Android Chrome enforces strict single-camera access and will throw
        // NotReadableError if the old camera is still held when getUserMedia is called.
        const previousStream = streamRef.current;
        if (previousStream) {
          previousStream.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
          setRawStream(null);
          if (previewRef.current) {
            previewRef.current.srcObject = null;
          }
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: audioConstraints,
        });

        // getUserMedia can resolve AFTER the component unmounted (the permission
        // prompt is slow, the user navigates away). Storing it then would leave a
        // live camera/mic with nothing mounted to stop it — the capture indicator
        // stays on for the rest of the session.
        if (isUnmountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return null;
        }

        streamRef.current = stream;
        setRawStream(stream);
        if (previewRef.current) {
          previewRef.current.srcObject = stream;
          await previewRef.current.play().catch((err) => {
            errorReporter.expected('Video preview autoplay failed', err, {
              feature: 'video',
              operation: 'previewPlay',
            });
          });
        }
        setError(null);

        const videoTrack = stream.getVideoTracks()[0];
        const settings = videoTrack?.getSettings();
        if (settings?.deviceId) {
          setSelectedCameraId(settings.deviceId);
        } else if (deviceId) {
          setSelectedCameraId(deviceId);
        }

        if (settings?.facingMode === 'user' || settings?.facingMode === 'environment') {
          setPreferredFacingMode(settings.facingMode);
        } else if (facing) {
          setPreferredFacingMode(facing);
        }

        const audioTrack = stream.getAudioTracks()[0];
        const audioSettings = audioTrack?.getSettings();
        const resolvedMicId = audioSettings?.deviceId ?? audioDeviceId;
        if (resolvedMicId) {
          setSelectedMicId(resolvedMicId);
          try {
            localStorage.setItem('notego_preferred_mic', resolvedMicId);
          } catch {
            /* quota */
          }
        }

        // Refresh available cameras and mics after stream is set
        await Promise.all([refreshCameras(), refreshMics()]);

        return stream;
      } catch (err: unknown) {
        let errorMessage = 'Unable to access camera or microphone.';

        const errorObj = err as { name?: string };
        if (errorObj.name === 'NotAllowedError' || errorObj.name === 'PermissionDeniedError') {
          errorMessage =
            'Permission denied to access camera and microphone. Please allow access in your browser settings.';
        } else if (errorObj.name === 'NotFoundError' || errorObj.name === 'DevicesNotFoundError') {
          errorMessage = 'No camera or microphone found. Please connect a device and try again.';
        } else if (errorObj.name === 'NotReadableError' || errorObj.name === 'TrackStartError') {
          errorMessage = 'Camera or microphone is already in use by another application.';
        } else if (errorObj.name === 'OverconstrainedError') {
          // Stale mic or camera constraint — clear stored mic and retry with defaults
          try {
            localStorage.removeItem('notego_preferred_mic');
          } catch {
            /* noop */
          }
          setSelectedMicId(null);

          try {
            const fallbackStream = await navigator.mediaDevices.getUserMedia({
              video: videoConstraints,
              audio: true,
            });

            if (isUnmountedRef.current) {
              fallbackStream.getTracks().forEach((track) => track.stop());
              return null;
            }

            streamRef.current = fallbackStream;
            setRawStream(fallbackStream);
            if (previewRef.current) {
              previewRef.current.srcObject = fallbackStream;
              await previewRef.current.play().catch(() => {});
            }
            setError(null);

            const fallbackVideoTrack = fallbackStream.getVideoTracks()[0];
            const fallbackSettings = fallbackVideoTrack?.getSettings();
            if (fallbackSettings?.deviceId) setSelectedCameraId(fallbackSettings.deviceId);
            if (fallbackSettings?.facingMode === 'user' || fallbackSettings?.facingMode === 'environment') {
              setPreferredFacingMode(fallbackSettings.facingMode);
            }

            const fallbackAudioTrack = fallbackStream.getAudioTracks()[0];
            const fallbackAudioSettings = fallbackAudioTrack?.getSettings();
            if (fallbackAudioSettings?.deviceId) {
              setSelectedMicId(fallbackAudioSettings.deviceId);
              try {
                localStorage.setItem('notego_preferred_mic', fallbackAudioSettings.deviceId);
              } catch {
                /* quota */
              }
            }

            await Promise.all([refreshCameras(), refreshMics()]);
            return fallbackStream;
          } catch {
            errorMessage = 'Could not satisfy the requested media constraints. Please try again.';
          }
        }

        setError(errorMessage);
        return null;
      }
    },
    [preferredFacingMode, refreshCameras, refreshMics],
  );

  // Serialize stream requests: concurrent calls (rapid aspect-ratio toggles re-firing
  // the auto-acquire effect, camera flips) would each see streamRef.current === null,
  // both await getUserMedia, and orphan the first-resolved stream — camera light stays
  // on and Android Chrome throws NotReadableError on the next acquire. Chaining each
  // request after the previous one guarantees the LAST requested constraints win and
  // every acquisition stops the prior stream before grabbing the hardware.
  const requestStream = useCallback(
    (options: RequestStreamOptions = {}): Promise<MediaStream | null> => {
      const next = requestChainRef.current.then(() => acquireStream(options));
      // Keep the chain alive even if a request unexpectedly rejects
      requestChainRef.current = next.catch(() => null);
      return next;
    },
    [acquireStream],
  );

  const selectCamera = useCallback(
    async (deviceId: string, options: Omit<RequestStreamOptions, 'deviceId'> = {}): Promise<void> => {
      if (!deviceId) {
        return;
      }

      setIsSwitchingCamera(true);
      try {
        // Preserve current mic selection when switching cameras
        const currentMicId = streamRef.current?.getAudioTracks()[0]?.getSettings().deviceId;
        await requestStream({
          deviceId,
          ...(currentMicId ? { audioDeviceId: currentMicId } : {}),
          ...options,
        });
      } finally {
        setIsSwitchingCamera(false);
      }
    },
    [requestStream],
  );

  const selectMic = useCallback(
    async (deviceId: string, options: Omit<RequestStreamOptions, 'audioDeviceId'> = {}): Promise<void> => {
      if (!deviceId) {
        return;
      }

      setIsSwitchingMic(true);
      try {
        // Preserve current camera selection when switching mics
        const currentCameraId = streamRef.current?.getVideoTracks()[0]?.getSettings().deviceId;
        await requestStream({
          audioDeviceId: deviceId,
          ...(currentCameraId ? { deviceId: currentCameraId } : {}),
          ...options,
        });
      } finally {
        setIsSwitchingMic(false);
      }
    },
    [requestStream],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearCameraWarning = useCallback(() => {
    setCameraWarning(null);
  }, []);

  const clearMicWarning = useCallback(() => {
    setMicWarning(null);
  }, []);

  // Mark unmounted so a late getUserMedia resolution can stop its own stream,
  // and stop anything still held when the hook goes away.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  // Handle device change events
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) {
      return undefined;
    }

    const handleDeviceChange = () => {
      void refreshCameras();
      void refreshMics();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [refreshCameras, refreshMics]);

  return {
    stream: streamRef.current,
    rawStream,
    previewRef,
    availableCameras,
    selectedCameraId,
    preferredFacingMode,
    isSwitchingCamera,
    cameraWarning,
    availableMics,
    selectedMicId,
    isSwitchingMic,
    micWarning,
    error,
    selectCamera,
    selectMic,
    refreshCameras,
    refreshMics,
    requestStream,
    cleanupStream,
    clearError,
    clearCameraWarning,
    clearMicWarning,
  };
}
